import { create } from 'zustand';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

// Request status
export type AccessRequestStatus = 'pending' | 'approved' | 'rejected';

// Access request data
export interface AccessRequest {
  id: string;
  fromUserId: string;
  toUserId: string;
  status: AccessRequestStatus;
  message?: string;
  requestedAt: Date;
  respondedAt?: Date;
  // Populated fields for display
  fromUser?: {
    displayName: string;
    iconUrl?: string;
  };
  toUser?: {
    displayName: string;
    iconUrl?: string;
  };
}

interface AccessRequestState {
  // State
  requests: AccessRequest[];
  sentRequests: AccessRequest[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;

  // Actions - For Donors (receiving requests)
  fetchReceivedRequests: (donorId: string) => Promise<void>;
  approveRequest: (requestId: string) => Promise<void>;
  rejectRequest: (requestId: string) => Promise<void>;

  // Actions - For Donees (sending requests)
  fetchSentRequests: (doneeId: string) => Promise<void>;
  sendRequest: (fromUserId: string, toUserId: string, message?: string) => Promise<void>;
  checkRequestStatus: (fromUserId: string, toUserId: string) => Promise<AccessRequestStatus | null>;

  // Common
  clearError: () => void;
  reset: () => void;
}

export const useAccessRequestStore = create<AccessRequestState>((set, get) => ({
  requests: [],
  sentRequests: [],
  isLoading: false,
  isSending: false,
  error: null,

  // Fetch requests received by donor
  fetchReceivedRequests: async (donorId: string) => {
    try {
      set({ isLoading: true, error: null });

      const requestsRef = collection(db, 'profileAccessRequests');
      const q = query(
        requestsRef,
        where('toUserId', '==', donorId),
        orderBy('requestedAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(q);

      const requests: AccessRequest[] = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          
          // Get fromUser info
          let fromUser = undefined;
          try {
            const userRef = doc(db, 'users', data.fromUserId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              fromUser = {
                displayName: userData.displayName || 'Unknown',
                iconUrl: userData.iconUrl,
              };
            }
          } catch (e) {
            console.warn('Could not fetch from user:', e);
          }

          return {
            id: docSnap.id,
            fromUserId: data.fromUserId,
            toUserId: data.toUserId,
            status: data.status,
            message: data.message,
            requestedAt: data.requestedAt?.toDate() || new Date(),
            respondedAt: data.respondedAt?.toDate(),
            fromUser,
          };
        })
      );

      set({ requests });
    } catch (error) {
      console.error('Error fetching received requests:', error);
      set({ error: '閲覧申請の取得に失敗しました' });
    } finally {
      set({ isLoading: false });
    }
  },

  // Approve request
  approveRequest: async (requestId: string) => {
    try {
      set({ isLoading: true, error: null });

      const requestRef = doc(db, 'profileAccessRequests', requestId);
      await updateDoc(requestRef, {
        status: 'approved',
        respondedAt: serverTimestamp(),
      });

      // Update local state
      set((state) => ({
        requests: state.requests.map((req) =>
          req.id === requestId
            ? { ...req, status: 'approved' as const, respondedAt: new Date() }
            : req
        ),
      }));
    } catch (error) {
      console.error('Error approving request:', error);
      set({ error: '申請の承認に失敗しました' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Reject request
  rejectRequest: async (requestId: string) => {
    try {
      set({ isLoading: true, error: null });

      const requestRef = doc(db, 'profileAccessRequests', requestId);
      await updateDoc(requestRef, {
        status: 'rejected',
        respondedAt: serverTimestamp(),
      });

      // Update local state
      set((state) => ({
        requests: state.requests.map((req) =>
          req.id === requestId
            ? { ...req, status: 'rejected' as const, respondedAt: new Date() }
            : req
        ),
      }));
    } catch (error) {
      console.error('Error rejecting request:', error);
      set({ error: '申請の拒否に失敗しました' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Fetch requests sent by donee
  fetchSentRequests: async (doneeId: string) => {
    try {
      set({ isLoading: true, error: null });

      const requestsRef = collection(db, 'profileAccessRequests');
      const q = query(
        requestsRef,
        where('fromUserId', '==', doneeId),
        orderBy('requestedAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(q);

      const sentRequests: AccessRequest[] = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          
          // Get toUser info
          let toUser = undefined;
          try {
            const userRef = doc(db, 'users', data.toUserId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              toUser = {
                displayName: userData.displayName || 'Unknown',
                iconUrl: userData.iconUrl,
              };
            }
          } catch (e) {
            console.warn('Could not fetch to user:', e);
          }

          return {
            id: docSnap.id,
            fromUserId: data.fromUserId,
            toUserId: data.toUserId,
            status: data.status,
            message: data.message,
            requestedAt: data.requestedAt?.toDate() || new Date(),
            respondedAt: data.respondedAt?.toDate(),
            toUser,
          };
        })
      );

      set({ sentRequests });
    } catch (error) {
      console.error('Error fetching sent requests:', error);
      set({ error: '送信済み申請の取得に失敗しました' });
    } finally {
      set({ isLoading: false });
    }
  },

  // Send new request
  sendRequest: async (fromUserId: string, toUserId: string, message?: string) => {
    try {
      set({ isSending: true, error: null });

      const [fromUserSnap, toUserSnap] = await Promise.all([
        getDoc(doc(db, 'users', fromUserId)),
        getDoc(doc(db, 'users', toUserId)),
      ]);
      if (!fromUserSnap.exists() || !toUserSnap.exists()) {
        throw new Error('申請対象のユーザー情報が見つかりません');
      }

      const fromUserData = fromUserSnap.data() as { role?: unknown; identityVerified?: unknown };
      const toUserData = toUserSnap.data() as { role?: unknown; status?: unknown };

      if (fromUserData.role !== 'donee') {
        throw new Error('閲覧申請はドニーのみ送信できます');
      }
      if (fromUserData.identityVerified !== true) {
        throw new Error('閲覧申請には本人確認が必要です');
      }
      if (toUserData.role !== 'donor' || toUserData.status !== 'approved') {
        throw new Error('このドナーは現在、閲覧申請を受け付けていません');
      }

      // Check if request already exists
      const existingStatus = await get().checkRequestStatus(fromUserId, toUserId);
      if (existingStatus) {
        throw new Error('すでに申請を送信済みです');
      }

      // Create new request
      const requestId = `${fromUserId}_${toUserId}`;
      const requestRef = doc(db, 'profileAccessRequests', requestId);
      await setDoc(requestRef, {
        fromUserId,
        toUserId,
        status: 'pending',
        message: message || null,
        requestedAt: serverTimestamp(),
      });

    } catch (error) {
      console.error('Error sending request:', error);
      set({ error: error instanceof Error ? error.message : '閲覧申請の送信に失敗しました' });
      throw error;
    } finally {
      set({ isSending: false });
    }
  },

  // Check if request exists between two users
  checkRequestStatus: async (fromUserId: string, toUserId: string) => {
    try {
      const requestsRef = collection(db, 'profileAccessRequests');
      const q = query(
        requestsRef,
        where('fromUserId', '==', fromUserId),
        where('toUserId', '==', toUserId),
        limit(1)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      return snapshot.docs[0].data().status as AccessRequestStatus;
    } catch (error) {
      console.error('Error checking request status:', error);
      return null;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      requests: [],
      sentRequests: [],
      isLoading: false,
      isSending: false,
      error: null,
    });
  },
}));
